import React, {Key} from 'react';
import memoize from 'memoize-one';
import {curry} from 'ramda';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import {ListItemKeySelector} from 'react-window';
import {noop} from '@pluto/utils';
import Header from './Header';
import Body from './Body';
import './Table.scss';
import {
    ListTableColumn,
    OnRowClickCallback,
    RowData,
} from './tableTypes';
import Empty from './Empty';

export interface ListTableProps {
    data: any[];
    columns: ListTableColumn[];
    headerHeight: number;
    rowHeight: number;
    emptyText: string;
    itemKey: ListItemKeySelector;
    onRowClick?: OnRowClickCallback;
    selectedRowKey?: Key;
}

type SameIndexProps = {
    items: any[],
    itemKey: ListItemKeySelector,
    selectedRowKey: Key,
}

export const sameIndex = curry((props: SameIndexProps, _item: any, index: number) => {
    const {
        itemKey,
        items,
        selectedRowKey,
    } = props;

    return itemKey(index, {
        items,
    }) === selectedRowKey;
});

export const getSelectedItemIndex = (items: any[], itemKey: ListItemKeySelector, selectedRowKey?: Key): number => {
    if (selectedRowKey === undefined || items.length === 0)
        return -1;

    const handleSameIndex = sameIndex({
        items,
        itemKey,
        selectedRowKey,
    });

    return items.findIndex(handleSameIndex);
};

export const createItemData = memoize((columns, items, rowHeight, itemKey, onRowClick, selectedRowKey): RowData => {
    const selectedRowIndex = getSelectedItemIndex(items, itemKey, selectedRowKey);
    return {
        columns,
        items,
        rowHeight,
        itemKey,
        onRowClick,
        selectedRowKey,
        selectedRowIndex,
    };
});

export const getBody = (itemCount: number, itemData: RowData, emptyText: string) => {
    if (itemCount === 0)
        return <Empty text={emptyText} />;

    return (
        <Body
            itemCount={itemCount}
            itemData={itemData}
        />
    );
};

const ListTable = (props: ListTableProps) => {
    const {
        data,
        columns,
        headerHeight,
        rowHeight,
        emptyText,
        itemKey,
        onRowClick = noop,
        selectedRowKey,
    } = props;
    const itemData = createItemData(
        columns,
        data,
        rowHeight,
        itemKey,
        onRowClick,
        selectedRowKey,
    );
    const itemCount = data.length;
    const body = getBody(itemCount, itemData, emptyText);

    return (
        <Paper className="paper">
            <div className="root">
                <Table
                    className="table"
                    component="div"
                >
                    <Header
                        rowHeight={headerHeight}
                        columns={columns}
                    />
                    {body}
                </Table>
            </div>
        </Paper>
    );
};

export default ListTable;
